import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { apiGet, apiPost } from '../api';
import { ensureSocket, disconnectSocket } from '../socket';

import logo from '../assets/logo.png';
import { formatNumberPrecise } from '../Utils';
import Dashboard from './Dashboard';
import ReceivedHistory from '../History/ReceivedHistory';
import SendHistory from '../History/SendHistory';
import Profile from './Profile';

const sendCoin = (payload) => apiPost('/api/transaction/send', payload);

const Modal = ({ show, onClose, children }) => {
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="ui-card w-full max-w-[440px] p-6 sm:p-7 shadow-pop max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3.5 right-3.5 w-8 h-8 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-ink-600 transition-colors"
        >
          <i className="fas fa-xmark" />
        </button>
        {children}
      </div>
    </div>
  );
};

const SendCoinForm = ({ onSend, onClose, loading }) => {
  const [ID, setID] = useState('');
  const [retypeID, setRetypeID] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  // Both IDs must match before anything is sent — a coin send cannot be undone.
  const mismatch = retypeID.length > 0 && ID !== retypeID;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (ID !== retypeID) { setError("User IDs don't match. Please re-enter."); return; }
    if (parseFloat(amount) <= 0) { setError('Amount must be greater than zero.'); return; }
    onSend({ ID, amount });
  };

  return (
    <>
      <h2 className="text-base font-bold text-slate-100 flex items-center gap-2.5 mb-5 pr-8">
        <span className="w-8 h-8 rounded-lg bg-accent-soft border border-accent-line flex items-center justify-center">
          <i className="fas fa-paper-plane text-accent text-xs" />
        </span>
        Send Balance
      </h2>

      {error && <div className="ui-error mb-4"><i className="fas fa-circle-exclamation" />{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="ui-label">Receiver's User ID</label>
          <input type="text" className="ui-input" value={ID} onChange={(e) => setID(e.target.value)} placeholder="Enter user ID" required />
        </div>
        <div>
          <label className="ui-label">Retype Receiver's User ID</label>
          <input
            type="text"
            className={`ui-input ${mismatch ? 'border-rose-500/50' : ''}`}
            value={retypeID}
            onChange={(e) => setRetypeID(e.target.value)}
            placeholder="Retype user ID"
            required
          />
          {mismatch && <p className="text-2xs text-rose-400 mt-1.5">IDs do not match</p>}
        </div>
        <div>
          <label className="ui-label">Amount</label>
          <input type="number" className="ui-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required min="0.01" step="0.01" />
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" className="ui-btn-ghost flex-1" onClick={onClose} disabled={loading}>Cancel</button>
          <button type="submit" className="ui-btn-primary flex-1" disabled={loading || mismatch}>
            {loading ? <><span className="ui-spinner w-4 h-4" /> Sending…</> : 'Send'}
          </button>
        </div>
      </form>
    </>
  );
};

const navGroups = [
  { heading: 'Overview', items: [{ to: '/', icon: 'fa-gauge-high', label: 'Dashboard' }] },
  {
    heading: 'Finance',
    items: [
      { to: '/received', icon: 'fa-arrow-down-long', label: 'Received History' },
      { to: '/send', icon: 'fa-arrow-up-long', label: 'Send History' },
    ],
  },
  { heading: 'Account', items: [{ to: '/profile', icon: 'fa-user', label: 'Profile' }] },
];

const avatarColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];

const Reseller = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSendCoinModal, setShowSendCoinModal] = useState(false);
  const [balance, setBalance] = useState(0);
  const [resellerAuthId, setResellerAuthId] = useState(null);
  const [resellerCustomId, setResellerCustomId] = useState('');
  const [resellerName, setResellerName] = useState('');
  const [resellerProfilePicture, setResellerProfilePicture] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    try {
      disconnectSocket();
      await signOut(auth);
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
      alert('Logout failed. Please try again.');
    }
  }, [navigate]);

  const fetchProfile = useCallback(async () => {
    try {
      const { profile } = await apiGet('/api/account/me');
      if (profile) {
        setBalance(profile.Balance || 0);
        setResellerCustomId(profile.ID || '');
        setResellerName(profile.name || '');
        setResellerProfilePicture(profile.profilePicture || null);
        setError(null);
      } else {
        setError('Your data could not be loaded. Logging out.');
        setTimeout(handleLogout, 3000);
      }
    } catch {
      setError('Failed to connect to the database.');
    }
  }, [handleLogout]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setResellerAuthId(user.uid);
        fetchProfile();
      } else {
        setResellerAuthId(null);
        setBalance(0);
        setResellerCustomId('');
        setResellerName('');
        navigate('/');
      }
    });
    // Balance updates arrive live over the socket (account:updated); this poll
    // is just a slow safety net in case the socket drops.
    const poll = setInterval(() => { if (auth.currentUser) fetchProfile(); }, 60000);
    return () => { unsubscribeAuth(); clearInterval(poll); };
  }, [fetchProfile, navigate]);

  // Live balance push — replaces the resellers/{uid} onSnapshot.
  useEffect(() => {
    let sock;
    let alive = true;
    ensureSocket().then((s) => {
      if (!alive || !s) return;
      sock = s;
      s.on('account:updated', fetchProfile);
    });
    return () => { alive = false; if (sock) sock.off('account:updated', fetchProfile); };
  }, [fetchProfile]);

  const handleSendCoin = async ({ ID, amount }) => {
    if (String(resellerCustomId) === ID) {
      alert('You cannot send coins to your own account.');
      return;
    }
    if (balance < parseFloat(amount)) {
      alert('Insufficient balance to complete this transaction.');
      return;
    }
    setLoading(true);
    try {
      const result = await sendCoin({ senderCollection: 'resellers', recipientIdStr: ID, amountStr: amount });
      // A callable threw on business failure; the REST endpoint answers 200
      // with success:false, so surface that as an error rather than a success
      // alert followed by an unchanged balance.
      if (result.success === false) throw new Error(result.message);
      alert(result.message);
      setShowSendCoinModal(false);
      fetchProfile();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="ui-error max-w-md"><i className="fas fa-triangle-exclamation" />{error}</div>
    </div>
  );

  if (!resellerAuthId) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <span className="ui-spinner w-10 h-10" />
      <p className="text-sm text-slate-500">Loading reseller data…</p>
    </div>
  );

  const initials = (resellerName || 'RS').slice(0, 2).toUpperCase();
  const avatarBg = resellerName
    ? avatarColors[resellerName.charCodeAt(0) % avatarColors.length]
    : '#3b82f6';

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 text-sm transition-colors ${
      isActive
        ? 'bg-accent-soft text-accent font-semibold'
        : 'text-slate-400 hover:text-slate-200 hover:bg-ink-600 font-medium'
    }`;

  return (
    <div className="min-h-screen">
      {/* ===== HEADER ===== */}
      <header className="sticky top-0 z-30 h-16 bg-ink-800/90 backdrop-blur border-b border-ink-500">
        <div className="h-full px-3 sm:px-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden w-9 h-9 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-ink-600 transition-colors"
              onClick={() => setSidebarOpen((p) => !p)}
              aria-label="Menu"
            >
              <i className={`fas ${sidebarOpen ? 'fa-xmark' : 'fa-bars'}`} />
            </button>
            <Link to="/" className="flex items-center gap-2.5 min-w-0">
              <img src={logo} alt="GolpoStream" className="w-9 h-9 rounded-lg object-cover border border-ink-500" />
              <span className="font-bold text-sm text-slate-100 truncate hidden sm:block">
                Golpo<span className="text-accent">Stream</span>
                <span className="text-slate-500 font-medium ml-1.5">Reseller</span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-ink-700 border border-ink-500">
              <i className="fas fa-wallet text-2xs text-accent" />
              <span className="text-sm font-bold text-slate-100 tabular-nums">
                {formatNumberPrecise(balance.toFixed(2))}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="w-9 h-9 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
              aria-label="Sign out"
              title="Sign out"
            >
              <i className="fa-solid fa-right-from-bracket text-sm" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Backdrop only exists below lg, where the sidebar slides over content */}
        {sidebarOpen && (
          <div className="fixed inset-0 top-16 z-30 bg-black/60 lg:hidden" onClick={closeSidebar} />
        )}

        {/* ===== SIDEBAR ===== */}
        <aside
          className={`fixed lg:sticky top-16 left-0 z-40 w-[264px] shrink-0
                      h-[calc(100vh-4rem)] bg-ink-800 border-r border-ink-500
                      flex flex-col transition-transform duration-200
                      ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        >
          <div className="p-4 border-b border-ink-500 shrink-0">
            <div className="flex items-center gap-3">
              {resellerProfilePicture ? (
                <img src={resellerProfilePicture} alt="" className="w-11 h-11 rounded-xl object-cover border border-ink-500 shrink-0" />
              ) : (
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-extrabold text-sm shrink-0"
                  style={{ background: avatarBg }}
                >
                  {initials}
                </div>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-sm text-slate-100 truncate">{resellerName || 'Reseller'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="ui-badge-info">Reseller</span>
                  <span className="text-2xs font-mono text-slate-500">{resellerCustomId}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl p-3.5 bg-ink-700 border border-ink-500">
              <p className="text-2xs font-semibold uppercase tracking-wider text-slate-500">Balance</p>
              <p className="text-2xl font-extrabold text-slate-100 mt-1 mb-3 tabular-nums leading-none">
                {formatNumberPrecise(balance.toFixed(2))}
              </p>
              <button onClick={() => setShowSendCoinModal(true)} className="ui-btn-primary w-full text-xs py-2">
                <i className="fas fa-paper-plane text-2xs" /> Send Balance
              </button>
            </div>
          </div>

          <nav className="p-3 flex-1 overflow-y-auto">
            {navGroups.map(({ heading, items }) => (
              <div key={heading} className="mb-4">
                <p className="text-2xs font-bold uppercase tracking-widest text-slate-600 px-3 pb-2">{heading}</p>
                {items.map(({ to, icon, label }) => (
                  <NavLink key={to} to={to} end={to === '/'} onClick={closeSidebar} className={navLinkClass}>
                    <i className={`fas ${icon} w-4 text-center text-xs shrink-0`} />
                    {label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="p-3 border-t border-ink-500 shrink-0">
            <button onClick={handleLogout} className="ui-btn-danger w-full text-xs py-2.5">
              <i className="fas fa-right-from-bracket text-2xs" /> Sign Out
            </button>
          </div>
        </aside>

        {/* ===== CONTENT ===== */}
        <main className="flex-1 min-w-0">
          <Routes>
            <Route path="/" element={<Dashboard userID={resellerAuthId} userType="reseller" />} />
            <Route path="/received" element={<ReceivedHistory userID={resellerAuthId} collectionName="resellers" />} />
            <Route path="/send" element={<SendHistory userID={resellerAuthId} collectionName="resellers" />} />
            <Route path="/profile" element={<Profile resellerID={resellerAuthId} />} />
          </Routes>
        </main>
      </div>

      <Modal show={showSendCoinModal} onClose={() => setShowSendCoinModal(false)}>
        <SendCoinForm onSend={handleSendCoin} onClose={() => setShowSendCoinModal(false)} loading={loading} />
      </Modal>
    </div>
  );
};

export default Reseller;
